import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const TYPES = ["Bridal Shower", "Team Building", "Kids Event", "Corporate", "Other"];
const inputClass = "w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none";
const EMPTY = { name: "", email: "", phone: "", event_type: "Bridal Shower", preferred_date: "", alternate_date: "", preferred_time: "", num_guests: "", notes: "" };

const F = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs tracking-[0.2em] uppercase font-bold text-[#121212] mb-2">{label}</span>
    {children}
  </label>
);

export default function PrivateEventInquiry() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) return toast.error("Please add your name, email and phone.");
    if (!form.num_guests || Number(form.num_guests) < 1) return toast.error("Please enter the number of guests.");
    if (!form.preferred_date) return toast.error("Please pick a preferred date.");
    setBusy(true);
    try {
      await api.post("/private-events/inquiries", { ...form, num_guests: Number(form.num_guests) });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <section id="private-event-inquiry" data-testid="private-event-section" className="bg-[#121212] text-[#F2E9D8] pt-24 lg:pt-28 pb-24 -mb-32 lg:pb-28 scroll-mt-24 relative">
      <div className="max-w-3xl mx-auto px-6 sm:px-12 lg:px-16">
        <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4 text-[#C6FF33]">Not a birthday?</p>
        <h2 className="font-serif font-black text-5xl lg:text-6xl leading-[0.95]">Private events.</h2>
        <p className="mt-5 text-[#F2E9D8]/75">Bridal showers, team building, corporate outings, kids groups — we'll host a private class just for your crew. You get a private booking link to share, and each guest pays for their own spot.</p>
        {done ? (
          <div data-testid="private-inquiry-success" className="mt-10 bg-[#C6FF33] text-[#121212] border-2 border-[#C6FF33] p-8">
            <p className="font-serif font-black text-3xl">Request received!</p>
            <p className="mt-2">Thanks, {form.name.split(" ")[0]}. We'll reach out within 1–2 business days to confirm your date and details.</p>
          </div>
        ) : (
          <div className="mt-10 bg-white text-[#121212] border-2 border-[#C6FF33] p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Name"><input data-testid="pe-name" value={form.name} onChange={set("name")} className={inputClass} /></F>
              <F label="Email"><input data-testid="pe-email" type="email" value={form.email} onChange={set("email")} className={inputClass} /></F>
              <F label="Phone number"><input data-testid="pe-phone" value={form.phone} onChange={set("phone")} className={inputClass} /></F>
              <F label="Event type">
                <select data-testid="pe-event-type" value={form.event_type} onChange={set("event_type")} className={inputClass}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </F>
              <F label="Preferred date"><input data-testid="pe-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputClass} /></F>
              <F label="Alternate date (optional)"><input data-testid="pe-alt-date" type="date" value={form.alternate_date} onChange={set("alternate_date")} className={inputClass} /></F>
              <F label="Preferred time"><input data-testid="pe-time" value={form.preferred_time} onChange={set("preferred_time")} placeholder="e.g. Saturday afternoon, 6 PM" className={inputClass} /></F>
              <F label="Number of guests"><input data-testid="pe-guests" type="number" min={1} value={form.num_guests} onChange={set("num_guests")} className={inputClass} /></F>
            </div>
            <F label="Anything else? (optional)"><textarea data-testid="pe-notes" rows={3} value={form.notes} onChange={set("notes")} className={inputClass + " resize-none"} placeholder="Theme, ages, company name, dietary needs…" /></F>
            <button data-testid="pe-submit" onClick={submit} disabled={busy}
              className="w-full px-8 py-4 bg-[#121212] text-[#C6FF33] hover:bg-black disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold transition-colors">
              {busy ? "Sending…" : "Request a Private Event"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
